<template>
  <div class="overflow-y-auto p-4">
    <el-card
      class="rounded-lg border bg-card text-card-foreground shadow-2xs mb-6"
    >
      <div class="flex flex-col space-y-1.5 p-6">
        <h3 class="text-2xl font-semibold leading-none tracking-tight">
          Filters &amp; Search
        </h3>
      </div>
      <el-form label-position="top">
        <div class="p-6 pt-0">
          <div class="grid gap-4 md:grid-cols-6">
            <div class="md:col-span-2">
              <div class="flex flex-col gap-2">
                <label
                  class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Search
                </label>
                <DebounceInput
                  placeholder="feedback by Event ID or description"
                  v-model="tableCtrl.queryParameters.search"
                  clearable
                >
                  <template #prepend>
                    <i class="el-icon-search"></i>
                  </template>
                </DebounceInput>
              </div>
            </div>
            <div class="flex flex-col gap-2">
              <label
                class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Feedback type
              </label>
              <SmartSelect
                v-model="tableCtrl.queryParameters.type"
                :add-all-option="{ key: undefined, value: 'All Types' }"
                :options="[
                  { id: 'True', name: 'True' },
                  { id: 'False', name: 'False' },
                ]"
              />
            </div>
            <div class="flex flex-col gap-2 col-span-2">
              <label
                class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Date Range
              </label>
              <DatePickerStartEnd
                :startDate.sync="tableCtrl.queryParameters.startTime"
                :endDate.sync="tableCtrl.queryParameters.endTime"
                type="daterange"
                start-placeholder="Start date"
                end-placeholder="End date"
                range-separator="To"
              />
            </div>
          </div>
        </div>
      </el-form>
    </el-card>
    <el-card class="rounded-lg border bg-card text-card-foreground shadow-2xs">
      <div class="flex flex-row justify-between items-center py-4">
        <div>
          <span> {{ tableCtrl.selectedCount }} selected </span>
        </div>
        <div class="flex flex-row">
          <el-button
            type="primary"
            icon="el-icon-cpu"
            @click="handleLearning()"
          >
            AI Learning
          </el-button>
          <el-button
            type="danger"
            icon="el-icon-delete"
            @click="handleBatchDelete()"
          >
            Delete
          </el-button>
        </div>
      </div>
      <div class="p-0">
        <div class="relative w-full overflow-auto">
          <el-table
            ref="tableRef"
            v-loading="tableCtrl.state.loading"
            element-loading-background="rgba(24, 33, 48, 0.8)"
            :data="tableCtrl.state.tableData"
            style="width: 100%"
            @selection-change="handleSelectionChange"
          >
            <el-table-column type="selection" width="55" />
            <el-table-column type="expand">
              <template #default="{ row }">
                <div class="p-4 space-y-4">
                  <div class="grid gap-4 md:grid-cols-2">
                    <div>
                      <h4 class="font-medium mb-2">Event Details</h4>
                      <div class="space-y-2 text-sm">
                        <span class="font-medium">AI Analysis:</span>
                        <div>
                          {{
                            row.event?.agentInfo?.reply ??
                            "no event agent reply"
                          }}
                        </div>
                        <div>
                          <span class="font-medium"> Related Devices: </span>
                          <div
                            v-if="row.event?.assetName"
                            class="flex flex-wrap gap-1 mt-1"
                          >
                            <div
                              class="inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground text-xs"
                              data-v0-t="badge"
                            >
                              {{
                                row.event?.assetName ?? "no event asset name"
                              }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 class="font-medium mb-2">User Feedback</h4>
                      <div class="space-y-2 text-sm">
                        <div>
                          <span class="font-medium">Comments:</span>
                          <p class="mt-1 p-2 bg-background rounded border">
                            {{ row.feedback }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Feedback ID" prop="id" min-width="120">
              <template slot-scope="scope">
                &#35; {{ scope.row.id ?? "" }}
              </template>
            </el-table-column>
            <el-table-column label="Event ID" prop="event.id" min-width="120">
              <template slot-scope="scope">
                &#35; {{ scope.row.event?.id ?? "" }}
              </template>
            </el-table-column>
            <el-table-column label="Feedback type" prop="type" min-width="120">
              <template slot-scope="scope">
                <div
                  v-if="scope.row.type == 'True'"
                  class="flex flex-row items-center gap-2"
                >
                  <i class="icon-thumbs-up text-green-600 w-4 h-4 mb-2"></i>
                  <span class="text-green-600">True</span>
                </div>
                <div
                  v-else-if="scope.row.type == 'False'"
                  class="flex flex-row items-center gap-2"
                >
                  <i class="icon-thumbs-down text-red-600 w-4 h-4 mb-2"></i>
                  <span class="text-red-600">False</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Description" min-width="280">
              <template slot-scope="scope">
                <div>
                  <div class="font-medium" style="word-break: break-word">
                    {{ scope.row.event?.name ?? "no event name" }}
                  </div>
                  <div
                    class="text-xs text-muted-foreground"
                    style="word-break: break-word"
                  >
                    {{ scope.row.event?.event ?? "no event key" }}
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="AI model" min-width="160">
              <template slot-scope="scope">
                <div class="flex items-center gap-2">
                  <SvgIcon :src="BotSvg" inline customClass="h-4 w-4" />
                  <span class="text-sm">
                    {{ scope.row.agentId }}
                  </span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Timestamp" min-width="160">
              <template slot-scope="scope">
                <div class="text-sm">
                  {{ scope.row.createdAt | formatTimestamp }}
                </div>
              </template>
            </el-table-column>

            <el-table-column
              label="Actions"
              width="180"
              align="right"
              fixed="right"
            >
              <template slot-scope="scope">
                <ActionButtons
                  :actions="actions"
                  :item="scope.row"
                  @command="handleCommand"
                />
              </template>
            </el-table-column>
          </el-table>
          <div class="flex mt-4 justify-end">
            <el-pagination
              @size-change="(val) => tableCtrl.setPageSize(val)"
              @current-change="(val) => tableCtrl.setCurrentPage(val)"
              :current-page="tableCtrl.state.pagination.page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="tableCtrl.state.pagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableCtrl.state.pagination.total"
            >
            </el-pagination>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import SmartSelect from "@/components/SmartControls/SmartSelect.vue";
import ActionButtons from "@/components/ActionButtons.vue";
import { TableController } from "@/controllers/TableController";
import BotSvg from "!!raw-loader!@/assets/bot.svg";
import SvgIcon from "@/components/SvgIcon.vue";
import BrainSvg from "!!raw-loader!@/assets/brain.svg";
import {
  getAiAuditList,
  deleteAiAuditById,
  getAgents,
  getAllAssetVo,
} from "@/api";
import { EventLogLevel, EventLogStatus, enumValueToLabel } from "@/enums";

export default {
  name: "AIFeedbackList",
  components: {
    SmartSelect,
    ActionButtons,
    SvgIcon,
  },
  emits: ["tabIndex"],
  data() {
    return {
      BotSvg,
      BrainSvg,
      tabIndex: 0,
      tableCtrl: new TableController({
        loadMethod: this.fetchData,
        autoLoad: true,
        isClientPaged: false,
        pageSize: 10,
        keyField: "id",
        totalField: "total",
        queryParameters: {
          agentId: undefined,
          type: undefined,
          search: undefined,
          startTime: undefined,
          endTime: undefined,
        },
      }),
    };
  },
  computed: {
    actions: () => [
      {
        label: "AI Learning",
        command: "ai-learning",
        icon: "el-icon-cpu",
        type: "button",
      },
      {
        type: "dropdown",
        label: "more",
        icon: "el-icon-more",
        items: [
          {
            label: "Edit",
            command: "edit",
            icon: "el-icon-edit",
          },
          {
            label: "Delete",
            command: "delete",
            icon: "el-icon-delete",
            color: "#F56C6C",
            divided: true,
          },
        ],
      },
    ],
    EventLogLevel: () => EventLogLevel,
    EventLogStatus: () => EventLogStatus,
  },
  methods: {
    enumValueToLabel,
    getAgents,
    getAllAssetVo,
    async handleRefresh() {
      await this.tableCtrl.refresh();
    },
    async fetchData(params) {
      const res = await getAiAuditList(params);
      return res;
    },
    updateValue(value) {
      this.emit("input", value);
    },
    async handleAddAudit() {
      const me = this;
      await me.openEditDialog(null, "edit");
      me.$message.success("Audit created successfully");
      await me.handleRefresh();
    },
    async handleCommand({ command, item }) {
      const me = this;
      if (["view", "edit", "feedback"].includes(command)) {
        await me.openEditDialog(item, command);
      } else if (command === "ai-learning") {
        await me.openChatDialog([item]);
      } else if (command === "delete") {
        await me.handleDelete(item);
      }
      me.$message.success("Operation successful");
      await me.handleRefresh();
    },
    async handleDelete(item) {
      const me = this;
      await me.$confirm(
        `Are you sure you want to delete ${item?.feedback}?`,
        "Warning",
        {
          type: "warning",
        }
      );
      await deleteAiAuditById(item?.id);
      me.$message.success("Delete successful");
    },
    async openEditDialog(item, command) {
      await this.$loaderWindow({
        title: `${command.capitalize()} Details`,
        component: () => import("./FeedbackEditDialog.vue"),
        props: {
          auditId: item?.id,
          isReadonly: command === "view",
        },
      });
    },
    handleChangeTab(index) {
      this.$emit("tabIndex", index);
    },
    async openChatDialog(items) {
      const aiAgent = items[0]?.event?.agentInfo;
      await this.$loaderWindow({
        title: `Chat with ${aiAgent?.agentId}`,
        component: () => import("@/pages/ai-agent/components/ChatDialog.vue"),
        height: "80%",
        width: "70%",
        showHeader: false,
        padding: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: {
          agent: {
            agent_id: aiAgent?.agentId,
            conversationId: aiAgent?.conversationId,
          },
          autoSendMessage: `Analyze and learn from the user feedback I selected (${items
            .map((item) => `#${item?.id}`)
            .join(", ")}). `,
        },
      });
    },
    handleSelectionChange(selection) {
      // Limit max selection to 10 items
      if (selection.length > 10) {
        this.$message.warning("You can select up to 10 items only.");
        selection = selection.slice(0, 10);
      }
      // Check all selected agentId are the same as the first
      if (selection.length > 1) {
        const firstAgentId = selection[0]?.agentId;
        const allSame = selection.every(
          (item) => item?.agentId === firstAgentId
        );
        if (!allSame) {
          this.$message.warning(
            "All selected items must have the same AI Agent."
          );
          selection = selection.filter(
            (item) => item?.agentId === firstAgentId
          );
        }
      }
      this.tableCtrl.handleSelectionChange(selection);
    },
    async handleSingleItemLearning(item) {
      await this.openChatDialog([item]);
    },
    async handleLearning() {
      if (this.tableCtrl.selectedCount === 0) {
        this.$message.warning(
          "Please select at least one feedback for AI learning."
        );
        return;
      } else {
        const items = this.tableCtrl.state.selectedItemsMap;
        await this.openChatDialog(items);
      }
    },
    async handleBatchDelete() {
      if (this.tableCtrl.selectedCount === 0) {
        this.$message.warning("Please select at least one feedback to delete.");
        return;
      }
      await this.$confirm(
        `Are you sure you want to delete ${this.tableCtrl.selectedCount} selected feedbacks?`,
        "Warning",
        {
          type: "warning",
        }
      );
      try {
        this.loading = true;
        for (const item of this.tableCtrl.state.selectedItemsMap) {
          await deleteAiAuditById(item?.id);
        }
        this.$message.success("Batch delete successful");
      } catch (error) {
        this.$message.error("Batch delete failed");
      } finally {
        this.loading = false;
      }
      await this.handleRefresh();
    },
  },
  watch: {
    "tableCtrl.queryParameters": {
      handler() {
        this.tableCtrl.setCurrentPage(1);
        this.handleRefresh();
      },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.tableCtrl.tableRef = this.$refs.tableRef;
    });
  },
};
</script>
